/**
 * Molecule: Toast + ToastContainer
 * Lightweight notification toasts. Use the useToast hook.
 *
 * Visual restyle D1d (2026-05-06): pill style now uses feedback.* tokens
 * with 15% alpha bg + full-strength border/text, lucide icons (no
 * unicode glyphs), 5s auto-dismiss, bottom-right desktop / top mobile
 * positioning.
 */
import React, { createContext, useContext, useCallback, useMemo, useState } from 'react'
import { Check, X, Info, AlertTriangle } from 'lucide-react'
import { tokens } from '@/design-system/tokens'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastAction {
  label: string
  onClick: () => void
}

interface Toast {
  id: string
  type: ToastType
  message: string
  action?: ToastAction
}

interface ToastOptions {
  action?: ToastAction
  /** ms before auto-dismiss. Undo toasts should use a longer window (6-8s) than the 5s default. */
  duration?: number
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, opts?: ToastOptions) => void
  success: (message: string, opts?: ToastOptions) => void
  error: (message: string, opts?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function variantTone(type: ToastType): { fg: string; Icon: React.ComponentType<any> } {
  switch (type) {
    case 'success': return { fg: tokens.color.feedback.success, Icon: Check as React.ComponentType<any> }
    case 'error':   return { fg: tokens.color.feedback.error,   Icon: X as React.ComponentType<any> }
    case 'warning': return { fg: tokens.color.feedback.warning, Icon: AlertTriangle as React.ComponentType<any> }
    default:        return { fg: tokens.color.feedback.info,    Icon: Info as React.ComponentType<any> }
  }
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { fg, Icon } = variantTone(toast.type)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: tokens.spacing[3],
      padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
      borderRadius: tokens.radius.md,
      border: `1px solid ${fg}`,
      background: fg + '22',
      color: tokens.color.text.primary,
      fontFamily: tokens.typography.fontFamily.sans,
      fontSize: tokens.typography.fontSize.sm,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      minWidth: 280, maxWidth: 400,
      animation: 'toastIn 0.2s ease',
      backdropFilter: 'blur(6px)',
    }}>
      <span style={{ color: fg, display: 'inline-flex', alignItems: 'center' }}>
        <Icon size={16} />
      </span>
      <span style={{ flex: 1 }}>{toast.message}</span>
      {toast.action && (
        <button
          onClick={() => { toast.action!.onClick(); onDismiss(toast.id) }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: fg, fontWeight: 700, fontSize: tokens.typography.fontSize.sm,
            padding: `2px ${tokens.spacing[2]}`, flexShrink: 0,
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: tokens.color.text.muted, padding: 2,
          display: 'inline-flex', alignItems: 'center',
        }}
      >
        <X size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((message: string, type: ToastType = 'info', opts?: ToastOptions) => {
    const id = Math.random().toString(36).slice(2)
    setToasts(prev => [...prev, { id, type, message, action: opts?.action }])
    setTimeout(() => dismiss(id), opts?.duration ?? 5000) // D1d: 5s auto-dismiss (longer for undo toasts)
  }, [dismiss])

  const success = useCallback((m: string, opts?: ToastOptions) => addToast(m, 'success', opts), [addToast])
  const error = useCallback((m: string, opts?: ToastOptions) => addToast(m, 'error', opts), [addToast])
  const value = useMemo<ToastContextValue>(() => ({
    toast: addToast,
    success,
    error,
  }), [addToast, success, error])

  return (
    <ToastContext.Provider value={value}>
      <style>{`
        @keyframes toastIn { from { transform: translateX(100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
        /* D1d — bottom-right on desktop, top-center on mobile. */
        .ml-toast-stack {
          position: fixed;
          bottom: ${tokens.spacing[6]};
          right: ${tokens.spacing[6]};
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: ${tokens.spacing[2]};
        }
        @media (max-width: 768px) {
          .ml-toast-stack {
            top: ${tokens.spacing[4]};
            bottom: auto;
            left: ${tokens.spacing[4]};
            right: ${tokens.spacing[4]};
            align-items: stretch;
          }
        }
      `}</style>
      {children}
      <div className="ml-toast-stack">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
